// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { findLastIndex } from 'lodash-es';
import { Colours } from '../../../../../base-styles/colours';
import { YearByYearDataSeries } from '../../../../../feature/dashboard/services/dashboard.service/helpers/year-by-year-data-series';
import { isClientNode } from '../../../../../functions/client-model/client-node/is-client-node';
import { isLeafNode } from '../../../../../functions/client-model/client-node/is-leaf-node';
import { ClientNode } from '../../../../../model/client-model/client-node';
import { Dict } from '../../../../../model/dict';
import { ABATEMENT_SERIES_ID } from '../../../../../public/dashboard/shared/dashboard.constants';
import { ClientBoardContainer } from '../../../../../public/dashboard/shared/model/client-board-container';
import { DashboardColours } from '../../../../../public/dashboard/shared/model/dashboard-colours';
import { dashboardStyles } from '../../../../../public/dashboard/v4/shared/dashboard.styles';
import { AreaSeriesStyle } from '../../../compound-renderers/chart-renderer/chart/series/area/area.series-style';
import { LineSeriesStyle } from '../../../compound-renderers/chart-renderer/chart/series/line/line.series-style';
import { Series } from '../../../compound-renderers/chart-renderer/chart/series/series';
import { SeriesType } from '../../../compound-renderers/chart-renderer/chart/series/series-type';
import { StyledSeries } from '../../../compound-renderers/chart-renderer/chart/series/styled-series';
import { DashPair } from '../../../renderers/styles/path/stroke/dash-pair';
import { RGBAColour } from '../../../renderers/styles/rgba-colour';
import { YearByYearDiagramData } from '../visualisers/year-by-year-visualiser/data/year-by-year-diagram.data';

@Injectable({
  providedIn: 'root',
})
export class YearByYearDataService {
  private _backgroundColour: RGBAColour;
  create(
    selected: ClientNode | ClientBoardContainer,
    seriesData: YearByYearDataSeries,
    colours: Dict<DashboardColours>,
    backgroundColour: string,
    highlightedYearIndex: number,
    hideWhenDeEmphasised: boolean = false
  ): YearByYearDiagramData {
    this._backgroundColour = RGBAColour.fromHex(backgroundColour);
    const hasTransition = !!seriesData.transitionData;
    const mainDiagramSeriesAlpha = hasTransition ? (hideWhenDeEmphasised ? 0 : 0.2) : 0.4;
    const selectedIsLeafNode = isClientNode(selected) ? isLeafNode(selected) : false;
    const projectedReduction = this._styleAsProjectedReduction(seriesData.projectedEmissionReduction);
    const remainingEmissionsDataSeries = seriesData.childRemainingEmissions ?? [seriesData.remainingEmissions];
    const remainingEmissions: StyledSeries[] = remainingEmissionsDataSeries.map((series) =>
      this._styleAsColouredAreaSeries(colours, series, mainDiagramSeriesAlpha, true)
    );
    const remainingEmissionsAndAbatementDataSeries =
      seriesData.childRemainingEmissionsAndAbatement ?? seriesData.remainingEmissionsAndAbatement;
    const remainingEmissionsAndAbatement = remainingEmissionsAndAbatementDataSeries.map((series) =>
      series.id === ABATEMENT_SERIES_ID
        ? this._styleAsColouredAreaSeriesWithColours(Colours.abatement, series, mainDiagramSeriesAlpha, true)
        : this._styleAsColouredAreaSeries(colours, series, mainDiagramSeriesAlpha, true)
    );
    const highlightColours = {};
    Object.entries(colours).forEach(([id, nodeColours]) => {
      highlightColours[id] = nodeColours.main;
    });
    highlightColours[ABATEMENT_SERIES_ID] = Colours.abatement.main;
    const mapEmissionSeries = (individualSeries: Series, index: number) => {
      if (index === 0) {
        return {
          ...individualSeries,
          style: this._getAreaSeriesStyle(Colours.emissionColourDark, mainDiagramSeriesAlpha, false),
        };
      }
      return this._styleAsColouredAreaSeries(colours, individualSeries, mainDiagramSeriesAlpha, false);
    };
    const emissionAndAbatement = seriesData.remainingEmissionsAndChildAbatement.map(mapEmissionSeries);
    const emissionAndLeafNodeAbatement = seriesData.remainingEmissionsAndLeafNodeAbatement.map(mapEmissionSeries);
    const historicalEmissionsDataSeries = seriesData.childHistoricalEmissions
      ? seriesData.childHistoricalEmissions
      : [seriesData.historicalEmissions];

    const historicalEmissions = historicalEmissionsDataSeries.map((series) =>
      this._styleAsColouredAreaSeries(colours, series, mainDiagramSeriesAlpha, true)
    );
    const historicalEmissionsOutcome = this._styleAsOutcomeLineSeries(seriesData.historicalEmissions);
    const data: YearByYearDiagramData = {
      emissionBau: this._styleAsEmissionBau(seriesData.emissionBau),
      emissionAndAbatement,
      emissionAndLeafNodeAbatement,
      remainingEmissions,
      remainingEmissionsAndAbatement,
      historicalEmissions,
      historicalEmissionsOutcome,
      inheritedHistoricalEmissions: seriesData.inheritedHistoricalEmissions,
      projectedReduction,
      leafNodeRemainingEmissions:
        seriesData.leafNodeRemainingEmissions?.map((series) =>
          this._styleAsColouredAreaSeries(colours, series, mainDiagramSeriesAlpha, false)
        ) ?? undefined,
      id: selected.id,
      interactive: !selectedIsLeafNode,
      mainDiagramDeEmphasised: selectedIsLeafNode,
      hideHighlightWhenDeEmphasised: hideWhenDeEmphasised,
      highlightedYearIndex,
      highlightColours,
    };
    if (seriesData.transitionData) {
      const t = seriesData.transitionData;
      const nodeColours = colours[selected.id];
      data.transition = {
        ...t,
        label: t.labelUnit,
        planned: t.planned ? this._styleAsTransitionSeries(nodeColours, t.planned) : undefined,
        outcome: t.outcome
          ? this._styleAsOutcomeLineSeries(
              t.outcome,
              findLastIndex(t.outcome.data, (seriesData) => seriesData.value !== undefined)
            )
          : undefined,
      };
    }

    return data;
  }

  private _getAreaSeriesStyle(colour: string, alpha: number, separator: boolean): AreaSeriesStyle {
    const baseColour = RGBAColour.fromHex(colour);
    const fillColour =
      alpha === 0 ? RGBAColour.fromHex(colour + '00') : RGBAColour.lerp(this._backgroundColour, baseColour, alpha);
    const style: AreaSeriesStyle = {
      fillColour,
      type: SeriesType.Area,
    };
    if (separator) {
      style.separator = {
        colour: dashboardStyles.colours.dashboardBackgroundColour,
        width: 1,
      };
    }
    return style;
  }

  private _styleAsColouredAreaSeries(
    colours: Dict<DashboardColours>,
    series: Series,
    alpha: number,
    separator: boolean
  ): StyledSeries {
    const dashboardColours = colours[series.id];
    return this._styleAsColouredAreaSeriesWithColours(dashboardColours, series, alpha, separator);
  }
  private _styleAsColouredAreaSeriesWithColours(
    colours: DashboardColours,
    series: Series,
    alpha: number,
    separator: boolean
  ): StyledSeries {
    let colour = colours?.dark ?? Colours.emissionColourDark;
    let highlightColour = colours?.main ?? Colours.emissionColour;
    return {
      ...series,
      style: this._getAreaSeriesStyle(colour, alpha, separator),
      highlightStyle: this._getAreaSeriesStyle(highlightColour, alpha, separator),
    };
  }

  private _styleAsDashedSeries(series: Series, colour: string, dash: DashPair): StyledSeries {
    const lineStyle: LineSeriesStyle = {
      type: SeriesType.Line,
      strokeStyle: {
        colour,
        width: 1,
        strokeType: {
          dash,
        },
      },
    };
    return { ...series, style: lineStyle };
  }

  private _styleAsEmissionBau(series: Series): StyledSeries {
    return this._styleAsDashedSeries(series, Colours.abatementStrokeColour, { dash: 14, gap: 2 });
  }

  private _styleAsOutcomeLineSeries(series: Series, focusedIndex?: number): StyledSeries {
    const colour = '#FFFFFF';

    const lineStyle: LineSeriesStyle = {
      dotStyle: {
        colour,
        diameter: 4,
      },
      focusedDotStyle: {
        colour,
        diameter: 9,
      },
      strokeStyle: {
        colour,
        width: 1,
      },
      type: SeriesType.Line,
      focusedIndex,
    };
    return { ...series, style: lineStyle };
  }

  private _styleAsProjectedReduction(series: Series): StyledSeries {
    return this._styleAsDashedSeries(series, '#FFFFFF', {
      dash: 4,
      gap: 3,
    });
  }

  private _styleAsTransitionSeries(nodeColours: DashboardColours, series: Series): StyledSeries {
    let colour = nodeColours.light;
    let style: LineSeriesStyle;
    style = {
      type: SeriesType.Line,
      strokeStyle: {
        colour,
        width: 1,
        strokeType: {
          dash: {
            dash: 3,
            gap: 4,
          },
        },
      },
      focusedDotStyle: {
        colour: nodeColours.main,
        outline: {
          colour,
          width: 1,
        },
        diameter: 9,
      },
      focusedIndex: series.data?.length - 1 ?? 0,
    };

    return { ...series, style };
  }
}
